import template from './multi-selection.component.html';
import './multi-selection.component.css';
import {lazyRegister} from '@/Core/lazyRegister';
import './multi-selection-inner/multi-selection-inner.component';
import {NumberAsBoolean} from "@/models";

export type OptionSupportType = string | number | boolean | NumberAsBoolean;

export type OptionModel = {
    text: string,
    title?: string,
    value?: OptionSupportType,
    checked?: boolean,
    disabled?: boolean,
    children?: OptionModel[],
    parentId?: string,
    [key: string]: any;
}

export function checkAllChildren(node: OptionModel, isChecked: boolean) {
    if (!node.children?.length) {
        return;
    }

    node.children.forEach((child) => {
        child.checked = isChecked;
        checkAllChildren(child, isChecked);
    });
}


class MultiSelectionController {
    static $inject = [
        '$element',
        '$timeout'
    ];

    private debounceChange: ng.IPromise<void>;
    isOpen: boolean = false;
    useSearch: boolean = false;
    multiple: boolean = true;
    showAllOption: boolean = true;
    required: boolean;
    placeholder: string;
    id: string;
    options: OptionModel[];
    totalSelected: number = 0;
    selectedItems: OptionModel[];
    selectedItem: OptionModel;
    ngModel: OptionSupportType[] | OptionSupportType;
    ngValue: OptionSupportType[] | OptionSupportType;
    selectedIds: OptionSupportType[];
    itemTemplate: string;
    hasIconAction: boolean;
    onChange: (args: { $event: OptionSupportType[] | OptionSupportType }) => void;
    onIconClick: () => void;

    private isInvalid: boolean;

    constructor(
        private $element: ng.IAugmentedJQuery,
        private $timeout: ng.ITimeoutService
    ) {
    }

    $onInit() {
        this.$element.addClass('multi-selection');

        if (this.ngValue !== undefined && this.ngModel === undefined) {
            this.ngModel = this.ngValue;
        }

        if (Array.isArray(this.ngModel) && this.ngModel.length) {
            this.selectedIds = this.ngModel;
        }

        if (this.selectedIds?.length) {
            this.updateSelectedItemText();
        }

        if (this.required && this.multiple) {
            this.showAllOption = false;
        }

        this.hasIconAction = typeof this.onIconClick == "function";
    }

    $onChanges(changes: ng.IOnChangesObject) {
        if (changes.ngValue) {
            this.ngModel = changes.ngValue.currentValue;
            if (Array.isArray(this.ngModel)) {
                this.selectedIds = this.ngModel;
            }

            this.updateSelectedItemText();
        } else if (!changes.options?.isFirstChange() && changes.options?.currentValue) {
            this.updateSelectedItemText();
        } else if (!changes.selectedIds?.isFirstChange()) {
            this.updateSelectedItemText();
        }
    }

    private updateSelectedItemText() {
        if (!this.multiple) {
            this.selectedItem = this.options?.find(x => x.value == this.ngModel);
            return;
        }

        this.totalSelected = this.selectedIds?.length || 0;
        this.checkItemById(this.options, this.selectedIds);
    }

    private checkItemById(items: OptionModel[], targetIds: OptionSupportType[]) {
        items.forEach(x => {
            x.checked = targetIds?.includes(x.value) ?? false;
            if (x.children?.length) {
                this.checkItemById(x.children, targetIds);
            }
        });
    }

    handleInnerChange(items: OptionModel[]) {
        if (this.debounceChange) {
            this.$timeout.cancel(this.debounceChange);
        }

        if (!this.multiple) {
            this.isOpen = false;
            const newItem = items?.length && !!items[0] ? items[0] : null;
            if (this.selectedItem?.value == newItem?.value) {
                return;
            }

            this.selectedItem = newItem;
            this.isInvalid = this.required && (this.selectedItem?.value == null ||
                typeof this.selectedItem?.value == "undefined" ||
                (typeof this.selectedItem?.value == 'string' && !this.selectedItem.value));
            this.ngModel = this.selectedItem?.value;
            this.debounceChange = this.$timeout(() => {
                this.onChange({$event: this.selectedItem?.value});
            }, 100);
            return;
        }

        this.isInvalid = this.required && !items?.length;
        this.totalSelected = items.length;
        this.selectedItems = items;
        this.ngModel = items.map(x => x.value);
        this.debounceChange = this.$timeout(() => {
            this.onChange({$event: this.ngModel});
        }, 500);
    }

    handleOpenMenu() {
        this.isOpen = !this.isOpen;
    }

    handleIconClick() {
        if (this.onIconClick) {
            this.onIconClick();
        }
    }
}

lazyRegister.component('multiSelection', {
    template: template,
    bindings: {
        selectedItems: '=?',
        selectedItem: '=?',
        ngModel: '=?',
        ngValue: '<?',
        multiple: '<?',
        selectedIds: '<?',
        useSearch: '<?',
        placeholder: '@?',
        itemTemplate: '@?',
        icon: '@?',
        iconTitle: '@?',
        error: '<?',
        required: '<?',
        showAllOption: '<?',
        options: '<',
        onIconClick: '&?',
        onChange: '&',
        id: '@?'
    },
    controller: MultiSelectionController
});
